import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClock } from 'react-icons/fa';
import { LoadingButton } from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { clearAuthSession } from '../utils/authSession';
import { PENDING_POLL_MS } from '../utils/coreOnboarding';
import './login.css';

function OnboardingPending() {
    const navigate = useNavigate();
    const { clearSessionExpired } = useAuth();
    const { fetchUserPermissions, hasFlowAccess, clearPermissions } = usePermissions();
    const [isChecking, setIsChecking] = useState(false);

    useEffect(() => {
        if (hasFlowAccess) {
            navigate('/home', { replace: true });
        }
    }, [hasFlowAccess, navigate]);

    const checkAccess = useCallback(async (showSpinner = false) => {
        if (showSpinner) {
            setIsChecking(true);
        }
        try {
            await fetchUserPermissions({ silent: true });
        } finally {
            if (showSpinner) {
                setIsChecking(false);
            }
        }
    }, [fetchUserPermissions]);

    useEffect(() => {
        checkAccess();
        const interval = setInterval(() => checkAccess(), PENDING_POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                checkAccess();
            }
        };
        const onFocus = () => checkAccess();
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
        };
    }, [checkAccess]);

    const handleSignOut = () => {
        clearAuthSession();
        clearPermissions();
        clearSessionExpired();
        navigate('/', { replace: true });
    };

    return (
        <div className="login-container">
            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-container">
                            <img
                                src="/favicon.ico"
                                alt="Wandawazi Flow Logo"
                                className="login-logo"
                                style={{ width: '120px', height: 'auto', maxHeight: '50px', objectFit: 'contain', filter: 'none' }}
                            />
                        </div>
                        <h1 className="login-title">Access requested</h1>
                        <p className="login-subtitle">An administrator still needs to approve your membership</p>
                    </div>

                    <div className="onboarding-status">
                        <div className="onboarding-status-icon">
                            <FaClock />
                        </div>
                        <p>
                            Your Flow access for the organisation you selected is being reviewed.
                            This page updates automatically when you are approved.
                        </p>
                        <LoadingButton
                            type="button"
                            className="login-btn"
                            variant="primary"
                            isLoading={isChecking}
                            loadingText="Checking..."
                            onClick={() => checkAccess(true)}
                        >
                            Check status
                        </LoadingButton>
                        <button
                            type="button"
                            className="forgot-password-link"
                            onClick={handleSignOut}
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OnboardingPending;
